import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useTasksData } from 'hooks/useTasksData';
import { useTasksFilters } from 'hooks/useTasksFilters';
import { FilterService } from 'primereact/api';
import { Button } from 'primereact/button';
import {
  DataTable,
  DataTablePageEvent,
  DataTableRowClickEvent,
  DataTableRowReorderEvent,
  DataTableSelectionSingleChangeEvent,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { TaskExportSettings } from 'types/interfaces/TaskExportSettings';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import {
  GlobalSettings,
  GlobalSettingsService
} from 'components/services/GlobalSettingsService';
import MapCard from 'ui/MapCard';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import { ImportErrorDialog } from 'ui/shared/ImportErrorDialog';

import { DepartmentsService } from '../../components/services';
import { CoordinatesService } from '../../components/services/CoordinatesService';
import { TaskService } from '../../components/services/TaskService';
import { TaskTemplate } from '../../constants';
import { DEFAULT_GLOBAL_SETTINGS } from '../../constants';
import { isSelected } from '../../helpers';
import saveFile from '../../helpers/saveFile';
import { useApiCall } from '../../hooks/useApiCall';
import { useColumnSettings } from '../../hooks/useColumnSettings';
import { useColumnSettingsObserver } from '../../hooks/useColumnSettingsObserver';
import {
  Coordinate,
  DropdownSelectorType,
  MapMessage,
  PageRequest,
  Task,
  TaskCoordinate,
  TreeNode
} from '../../types/interfaces';
import { findNodeByKey, hasChildren, TooltipPosition } from '../../utils';
import { transformVisibilityArrayToMap } from '../../utils/rolesHelpters';

import '../../ui/css/Tasks.css';
import { COLUMN_CONFIG, ColumnConfig } from './COLUMN_CONFIG';
import { selectAllId } from './constants';
import ExportDialog from './CustomExportDialog';
import DepartmentAssignmentDialog from './DepartmentAssignmentDialog';
import { getTaskColumns } from './getTaskColumns';
import TaskEmployeeDistributionCard from './taskEmployeeDistribution';
import TaskMobileView from './TaskMobileView';
import TasksTableHeader from './TasksTableHeader';

const storedDepartmentsFilter = 'storedDepartmentsFilter';
const withoutDepartmentsId = '1713383261709';

FilterService.register('custom_priority', (value, filters) => {
  return value;
});

FilterService.register('custom_detailType', (value, filters) => {
  return value;
});

FilterService.register('custom_status', (value, filters) => {
  return value;
});

const defaultMapMessage: MapMessage = {
  text: 'Исполнители отображаются по нарядам, отфильтрованным на странице нарядов',
  severity: 'info'
};

const errorMapMessage: MapMessage = {
  text: 'Исполнители не отображаются, так как не выбран конкретный участок и даты регистрации нарядов.',
  severity: 'error'
};

export default function Tasks() {
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [mapDialogVisible, setMapDialogVisible] = useState(false);
  const [mapOpenTime, setMapOpenTime] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ error: string }[]>(
    []
  );
  const [errorDialogVisible, setErrorDialogVisible] = useState<boolean>(false);

  const [taskToDelete, setTaskToDelete] = useState<string | number | null>(
    null
  );
  const [selectedTask, setSelectedTask] = useState<Task>(TaskTemplate);
  const [idTooltipPosition, setIdTooltipPosition] =
    useState<TooltipPosition>('top');
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [openDepartmentAssignmentDialog, setOpenDepartmentAssignmentDialog] =
    useState<boolean>(false);
  const [
    employeeDistributionDialogVisible,
    setEmployeeDistributionDialogVisible
  ] = useState(false);

  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>();

  const elementUIs = useSelector((state: RootState) => state.auth.elementUIs);
  const visibilityClasses = useMemo(
    () => transformVisibilityArrayToMap(elementUIs),
    [elementUIs]
  );
  const canFullTaskWorkOrderActions = useMemo(
    () =>
      visibilityClasses['wholeui__roles'] === true ||
      visibilityClasses['task-card-transitions-btns__roles'] === true,
    [visibilityClasses]
  );

  useEffect(() => {
    if (!canFullTaskWorkOrderActions) {
      setOpenDepartmentAssignmentDialog(false);
      setEmployeeDistributionDialogVisible(false);
    }
  }, [canFullTaskWorkOrderActions]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await GlobalSettingsService.get();
        setGlobalSettings(settings);
      } catch (error) {
        console.error('Ошибка загрузки настроек:', error);
      }
    };
    loadSettings();
  }, []);

  const [visibleColumns, setVisibleColumns] = useState<ColumnConfig[]>(() => {
    const saved = localStorage.getItem('visibleColumns');
    return saved ? JSON.parse(saved) : COLUMN_CONFIG.filter((c) => c.visible);
  });

  const tasksTableRef = useRef<HTMLDivElement>(null);

  const headerToFieldMap = useMemo(() => {
    const map = new Map<string, string>();
    visibleColumns.forEach((col) => {
      map.set(col.header, col.field);
    });
    return map;
  }, [visibleColumns]);

  // хук для управления настройками колонок
  const {
    columnOrder,
    columnWidths,
    handleColumnResizeEnd,
    updateColumnSettings
  } = useColumnSettings({
    orderStorageKey: 'tasksColumnOrder',
    widthsStorageKey: 'tasksColumnWidths',
    defaultOrder: COLUMN_CONFIG.filter((c) => c.visible).map((c) => c.field),
    defaultWidths: (() => {
      const widths: Record<string, string> = {};
      COLUMN_CONFIG.forEach((col) => {
        if (col.width) {
          widths[col.field] = col.width;
        }
      });
      return widths;
    })(),
    headerToFieldMap,
    tableRef: tasksTableRef,
    visibleColumns,
    allFields: COLUMN_CONFIG.map((c) => c.field)
  });

  const [isDepartmentsLoaded, setIsDepartmentsLoaded] = useState(false);
  const [treeDropDownValue, setTreeDropDownValue] = useState<TreeNode[]>([]);
  const [departments, setDepartments] = useState<TreeNode[]>([]);
  const [taskTypes, setTaskTypes] = useState<DropdownSelectorType[]>([]);
  const [taskStatuses, setTaskStatuses] = useState<DropdownSelectorType[]>([]);
  const [relevantCoordinates, setRelevantCoordinates] =
    useState<Coordinate[]>();
  const [expiredCoordinates, setExpiredCoordinates] = useState<Coordinate[]>();
  const [validTasks, setValidTasks] = useState<TaskCoordinate[]>([]);
  const [invalidTasks, setInvalidTasks] = useState<TaskCoordinate[]>([]);
  const [mapMessage, setMapMessage] = useState<MapMessage>(defaultMapMessage);
  const getTypesApi = useCallback(async () => {
    const types = await TaskService.getTaskTypes();
    if (types && types.data) setTaskTypes(types.data);
  }, []);
  useApiCall(getTypesApi);
  const getTaskStatuses = useCallback(async () => {
    const types = await TaskService.getTaskStatuses();
    if (types && types.data) setTaskStatuses(types.data);
  }, []);
  useApiCall(getTaskStatuses);
  const toast = useRef<Toast>(null);

  const isSelectedIdExist = isSelected(selectedTask);
  const { name } = selectedTask || {};

  const {
    searchFilter,
    setSearchFilter,
    statusFilterType,
    setStatusFilterType,
    filters,
    setFilters,
    selectedTaskTypeFilter,
    setSelectedTaskTypeFilter,
    selectedStatusFilter,
    setSelectedStatusFilter,
    selectedPriorityFilter,
    setSelectedPriorityFilter,
    selectedDateFilter,
    selectedDepartmentsFilter,
    setSelectedDepartmentsFilter,
    setPageNumber,
    pageSize,
    setPageSize,
    first,
    setFirst,
    multiSortMeta,
    setMultiSortMeta,

    filterRefs,

    onFiltersChange,
    handleInputChange,
    handleMultiselectFilterChange,
    clearArrayFilter,
    handleDateFilterChange,
    handleCustomFilterChange,
    debouncedHandleSelectionChange,
    handleDeleteTagItem,

    filtersForApi
  } = useTasksFilters(departments, isDepartmentsLoaded, setDepartments);

  const {
    assetFilterRef,
    actualStartDateFilterRef,
    creationDateFilterRef,
    taskNameFilterRef,
    departmentNameFilterRef,
    employeesFilterRef,
    idFilterRef,
    plannedStartDateFilterRef,
    plannedEndDateFilterRef,
    actualEndDateFilterRef
  } = filterRefs;

  const { tasks, loading, setLoading, totalCount, getTasksCallback, setTasks } =
    useTasksData(
      filtersForApi,
      isDepartmentsLoaded,
      selectedDepartmentsFilter,
      selectedDateFilter
    );

  const exportHistory = useCallback(async () => {
    const result = await TaskService.exportTasksHistoryExcel(filtersForApi);
    await saveFile(result.data, 'History.xls', '');
  }, [filtersForApi]);

  const exportTasks = useCallback(async () => {
    const result = await TaskService.exportTasksExcel(filtersForApi);
    await saveFile(result.data, 'Tasks.xls', '');
  }, [filtersForApi]);

  const exportTemplate = useCallback(async () => {
    const result = await TaskService.getTasksTemplateExcel();
    await saveFile(result.data, 'Шаблон Наряды.xls', '');
  }, []);

  const openMap = useCallback(async () => {
    let departmentsToUse = selectedDepartmentsFilter;
    let dateToUse = selectedDateFilter;
    const savedDepts = localStorage.getItem('storedDepartmentsFilter');
    if (savedDepts) {
      const parsed = JSON.parse(savedDepts);
      if (parsed.length) departmentsToUse = parsed;
    }
    const savedDate = localStorage.getItem('selectedDateFilter');
    if (savedDate) {
      const parsed = JSON.parse(savedDate);
      if (parsed.dateFrom && parsed.dateUntil) dateToUse = parsed;
    }
    const leafDepartments = (departmentsToUse as TreeNode[]).filter(
      (node) => !hasChildren(node, treeDropDownValue)
    );

    setMapDialogVisible(true);
    setRelevantCoordinates([]);
    setExpiredCoordinates([]);
    setValidTasks([]);
    setInvalidTasks([]);

    const isDepartmentValid =
      leafDepartments.length === 1 &&
      !leafDepartments.some(
        (node) => node.key === selectAllId || node.key === withoutDepartmentsId
      );

    const isDateValid = !!dateToUse.dateFrom || !!dateToUse.dateUntil;

    if (!isDepartmentValid || !isDateValid) {
      setMapMessage(errorMapMessage);
      setMapOpenTime(null);
      return;
    }

    const currentTime = new Date().toLocaleString();
    setMapOpenTime(currentTime);

    try {
      setLoading(true);
      const [coordinatesResult, tasksResult] = await Promise.all([
        CoordinatesService.getCoordinates(filtersForApi),
        TaskService.getTaskCoordinates(filtersForApi)
      ]);

      if (tasksResult.status === 200 && tasksResult.data) {
        const { valid, invalid } = tasksResult.data.reduce(
          (
            acc: { valid: any[]; invalid: any[] },
            task: { asset: { latitude: number; longitude: number } }
          ) => {
            const hasValidCoords =
              task.asset?.latitude &&
              task.asset?.longitude &&
              task.asset.latitude !== 0 &&
              task.asset.longitude !== 0;

            const modifiedTask = {
              ...task,
              coordinateType: 'task' as const
            };

            hasValidCoords
              ? acc.valid.push(modifiedTask)
              : acc.invalid.push(modifiedTask);
            return acc;
          },
          { valid: [] as TaskCoordinate[], invalid: [] as TaskCoordinate[] }
        );

        if (valid.length > 200) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Предупреждение',
            detail: 'Слишком много объектов для отображения (макс. 200)',
            life: 5000
          });
        } else {
          setValidTasks(valid);
        }

        setInvalidTasks(invalid);
      }

      if (coordinatesResult.data) {
        if (coordinatesResult.data.relevantCoordinates?.length > 200) {
          toast.current?.show({
            severity: 'error',
            summary: 'Ошибка',
            detail: 'Превышено максимальное число исполнителей',
            life: 5000
          });
          return;
        }

        setRelevantCoordinates(coordinatesResult.data.relevantCoordinates);
        setExpiredCoordinates(coordinatesResult.data.expiredCoordinates);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Ошибка загрузки данных',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  }, [
    filtersForApi,
    selectedDateFilter.dateFrom,
    selectedDateFilter.dateUntil,
    selectedDepartmentsFilter,
    setLoading,
    treeDropDownValue
  ]);

  const importTasksXLS = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Validate the file
      const validationResponse = await TaskService.import(formData, true);

      if (validationResponse?.data?.errors?.length > 0) {
        setValidationErrors(
          validationResponse.data.errors.map((i) => ({ error: i }))
        );
        setErrorDialogVisible(true);
        return;
      }

      // Import the file (PUT)
      const importResponse = await TaskService.import(formData);
      toast.current?.show({
        severity: 'success',
        summary: 'Импорт успешно завершен',
        detail: `${importResponse.data.tasks.length} нарядов импортировано.`,
        sticky: true
      });

      await getTasksCallback();
    } catch (error) {
      console.error('Ошибка импорта:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка импорта',
        detail: 'Произошла ошибка при импорте файла.',
        sticky: true
      });
    }
  };

  const exportWorks = useCallback(async () => {
    const result = await TaskService.exportWorksExcel(filtersForApi);
    await saveFile(result.data, 'Works.xls', '');
  }, [filtersForApi]);

  const handleExport = async (settings: Record<string, boolean>) => {
    const requestParams = {
      ...filtersForApi,
      ...settings // Combine PageRequest and TaskExportSettings
    } as PageRequest & Partial<TaskExportSettings>; // Explicitly cast

    const result = await TaskService.customExportExcel(requestParams);
    await saveFile(result.data, 'ExportedTasks.xls', '');
  };

  const onOpenCard = useCallback((task: Task) => {
    window.open(`/${task.id}`, '_blank');
  }, []);

  const dFilter = useMemo(() => {
    return localStorage.getItem(storedDepartmentsFilter);
  }, []);

  // Эффект для сохранения настроек
  useEffect(() => {
    localStorage.setItem('visibleColumns', JSON.stringify(visibleColumns));
    localStorage.setItem(
      'selectedDateFilter',
      JSON.stringify(selectedDateFilter)
    );
  }, [visibleColumns, selectedDateFilter]);

  useEffect(() => {
    localStorage.setItem('searchFilter', searchFilter);
  }, [searchFilter]);

  const getDepartmentsCallback = useCallback(async () => {
    try {
      setIsDepartmentsLoaded(false);
      const result = await DepartmentsService.getDepartments();
      if (!result.data || !Array.isArray(result.data)) {
        console.error('Invalid departments data');
        setIsDepartmentsLoaded(true);
        return;
      }
      setDepartments(result.data ? [...result.data] : []);
      setTreeDropDownValue(
        result.data
          ? [
              {
                key: selectAllId,
                label: 'Выбрать все участки'
              },
              {
                key: withoutDepartmentsId,
                label: 'Наряды без участков'
              },
              ...result.data
            ]
          : [
              { key: selectAllId, label: 'Выбрать все участки' },
              { key: withoutDepartmentsId, label: 'Наряды без участков' }
            ]
      );
      if (dFilter && result.data) {
        const storedFilter = JSON.parse(dFilter) as TreeNode[];

        // Валидация и синхронизация с актуальными данными
        const parsedDFilter = storedFilter.flatMap((storedNode) => {
          if (
            storedNode.key === selectAllId ||
            storedNode.key === withoutDepartmentsId
          ) {
            return [storedNode];
          }

          const actualNode = findNodeByKey(result.data!, storedNode.key);
          return actualNode ? [actualNode] : [];
        });

        const areFiltersEqual =
          parsedDFilter.length === storedFilter.length &&
          parsedDFilter.every((node, index) => {
            const original = storedFilter[index];

            if (
              original.key === selectAllId ||
              original.key === withoutDepartmentsId
            ) {
              return node.key === original.key;
            }

            return node.key === original.key && node.label === original.label;
          });

        if (!areFiltersEqual) {
          localStorage.setItem(
            storedDepartmentsFilter,
            JSON.stringify(parsedDFilter)
          );
        }
        setSelectedDepartmentsFilter(parsedDFilter);
        if (parsedDFilter.length === 0) {
          toast.current &&
            toast.current.show({
              severity: 'warn',
              summary: 'Предупреждение',
              detail: 'Выберите участки для отображения данных в таблице',
              life: 5000
            });
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки участков:', error);
      toast.current?.show({
        severity: 'error',
        detail: 'Не удалось загрузить участки'
      });
    } finally {
      setIsDepartmentsLoaded(true);
    }
  }, [dFilter]);

  useApiCall(getDepartmentsCallback);

  const confirmDeleteTemplate = () => {
    handleDelete(taskToDelete);
    setDeleteDialogVisible(false);
    setSelectedTask(TaskTemplate);
  };

  const mapDialogFooter = (
    <div>
      <div className="flex">
        <Button label="Обновить" onClick={openMap} className="p-button-info" />
        <Button
          label="Закрыть"
          onClick={() => MapDialogCloseHandle()}
          className="p-button-secondary"
        />
      </div>
    </div>
  );

  const handleUpdate = async () => {
    await getTasksCallback();
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await TaskService.deleteTask(id.toString());
      await getTasksCallback();
    }
  };

  const handleAdd = async (task: Task) => {
    await getTasksCallback();
  };

  useEffect(() => {
    const handleMessage = (event: {
      origin: string;
      data: {
        type: string;
        payload: {
          updatedDetail: [any, any];
          onAddProps: Task;
          taskToDelete: string | number | null;
        };
      };
    }) => {
      if (event.origin !== window.location.origin) {
        return; // Always check the origin
      }
      switch (event.data.type) {
        case 'TRANSITION_SUCCESS':
          handleUpdate();
          break;
        case 'UPDATE_SUCCESS':
          handleUpdate();
          break;
        case 'ADD_SUCCESS':
          handleAdd(event.data.payload.onAddProps);
          break;
        case 'DELETE_SUCCESS':
          handleDelete(event.data.payload.taskToDelete);
          break;
        default:
          break;
      }
    };

    // Add the event listener
    window.addEventListener('message', handleMessage);

    // Return a cleanup function to remove the event listener
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [tasks]);

  const header = (
    <TasksTableHeader
      handleFilterChange={handleCustomFilterChange}
      selectedDepartmentsFilter={selectedDepartmentsFilter.map((department) =>
        department.label === 'Выбрать все участки'
          ? { ...department, label: 'Выбраны все участки' }
          : department
      )}
      isMobile={isMobile}
      setIsMobile={setIsMobile}
      treeDropDownValue={treeDropDownValue}
      handleSelectionChange={debouncedHandleSelectionChange}
      handleDateFilterChange={handleDateFilterChange}
      datepickerDate={selectedDateFilter}
      handleDeleteTagItem={handleDeleteTagItem}
      importTasksXLS={importTasksXLS}
      exportTemplate={exportTemplate}
      openMap={openMap}
      exportHistory={exportHistory}
      exportTasks={exportTasks}
      exportWorks={exportWorks}
      customExportWorks={() => setDialogVisible(true)}
      isDepartmentsLoaded={isDepartmentsLoaded}
      setSelectedTask={setSelectedTask}
      toast={toast}
      isSelectedIdExist={isSelectedIdExist}
      onOpenCard={onOpenCard}
      selectedTask={selectedTask}
      setTaskToDelete={setTaskToDelete}
      setDeleteDialogVisible={setDeleteDialogVisible}
      columnsConfig={COLUMN_CONFIG}
      visibleColumns={visibleColumns}
      setVisibleColumns={setVisibleColumns}
      hasHorizontalScroll={hasHorizontalScroll}
      setSearchFilter={setSearchFilter}
      searchFilter={searchFilter}
      openDepartmentAssignmentDialog={openDepartmentAssignmentDialog}
      setOpenDepartmentAssignmentDialog={setOpenDepartmentAssignmentDialog}
      setEmployeeDistributionDialogVisible={
        setEmployeeDistributionDialogVisible
      }
    />
  );

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const tableOffset = useMemo(() => {
    return tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
  }, [tasks, selectedDepartmentsFilter]);

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement, tableOffset]);

  const onPageChanged = useCallback(async (event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setFirst(0);
      setPageSize(event.rows);
    }

    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);
  const onSelectionChangeCallback = useCallback(
    (e: DataTableSelectionSingleChangeEvent<Task[]>) =>
      setSelectedTask(e.value),
    []
  );
  const onRowDoubleClickCallback = useCallback(
    (e: DataTableRowClickEvent) => {
      const task = e.data as Task;
      setSelectedTask(task);
      onOpenCard(task);
    },
    [onOpenCard]
  );
  const onRowReorderCallback = useCallback(
    (e: DataTableRowReorderEvent<Task[]>) => setTasks(e.value),
    []
  );

  const MapDialogCloseHandle = () => {
    setMapDialogVisible(false);
    setMapMessage(defaultMapMessage);
  };

  const columns = useMemo(
    () =>
      getTaskColumns({
        clearArrayFilter,
        visibleColumns,
        handleInputChange,
        handleMultiselectFilterChange,
        selectedTaskTypeFilter,
        setSelectedTaskTypeFilter,
        selectedStatusFilter,
        setSelectedStatusFilter,
        selectedPriorityFilter,
        setSelectedPriorityFilter,
        taskTypes,
        taskStatuses,
        filters,
        setFilters,
        setPageNumber,
        setFirst,
        idFilterRef,
        taskNameFilterRef,
        departmentNameFilterRef,
        employeesFilterRef,
        assetFilterRef,
        creationDateFilterRef,
        actualStartDateFilterRef,
        plannedStartDateFilterRef,
        plannedEndDateFilterRef,
        actualEndDateFilterRef,
        columnOrder,
        columnWidths,
        setStatusFilterType,
        statusFilterType,
        idTooltipPosition,
        setIdTooltipPosition
      }),
    [
      clearArrayFilter,
      visibleColumns,
      handleInputChange,
      handleMultiselectFilterChange,
      selectedTaskTypeFilter,
      setSelectedTaskTypeFilter,
      selectedStatusFilter,
      setSelectedStatusFilter,
      selectedPriorityFilter,
      setSelectedPriorityFilter,
      taskTypes,
      taskStatuses,
      filters,
      setFilters,
      setPageNumber,
      setFirst,
      idFilterRef,
      taskNameFilterRef,
      departmentNameFilterRef,
      employeesFilterRef,
      assetFilterRef,
      creationDateFilterRef,
      actualStartDateFilterRef,
      plannedStartDateFilterRef,
      plannedEndDateFilterRef,
      actualEndDateFilterRef,
      columnOrder,
      columnWidths,
      setStatusFilterType,
      statusFilterType,
      idTooltipPosition,
      setIdTooltipPosition
    ]
  );

  const checkHorizontalScroll = useCallback(() => {
    // Проверяем наличие горизонтального скролла у #table-header
    const tableHeader = document.querySelector('.tasks-table #table-header');
    if (!tableHeader) {
      setHasHorizontalScroll(false);
      return;
    }

    // Проверяем наличие горизонтального скролла
    const hasHorizontal = tableHeader.scrollWidth > tableHeader.clientWidth;
    setHasHorizontalScroll(hasHorizontal);
  }, []);

  // хук для отслеживания изменений таблицы
  useColumnSettingsObserver({
    tableRef: tasksTableRef,
    updateColumnSettings,
    checkHorizontalScroll,
    dependencies: [tasks]
  });

  return (
    <div
      ref={tasksTableRef}
      className={
        !isMobile ? 'tasks-table wrapper wrapper--main' : 'tasks-table wrapper '
      }
    >
      <Toast ref={toast} />

      <ExportDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        onExport={handleExport}
      />

      <ImportErrorDialog
        errorDialogVisible={errorDialogVisible}
        setErrorDialogVisible={setErrorDialogVisible}
        validationErrors={validationErrors}
      />

      {canFullTaskWorkOrderActions && (
        <DepartmentAssignmentDialog
          openDepartmentAssignmentDialog={openDepartmentAssignmentDialog}
          setOpenDepartmentAssignmentDialog={setOpenDepartmentAssignmentDialog}
          departments={departments}
          globalSettings={globalSettings ?? DEFAULT_GLOBAL_SETTINGS}
        />
      )}

      {canFullTaskWorkOrderActions && (
        <Dialog
          visible={employeeDistributionDialogVisible}
          onHide={() => setEmployeeDistributionDialogVisible(false)}
          header="Распределение нарядов по сотрудникам"
          style={{
            width: '95vw',
            maxWidth: 2200,
            minWidth: 800,
            minHeight: 300,
            maxHeight: '92vh'
          }}
          contentStyle={{
            overflow: 'hidden',
            maxHeight: 'calc(92vh - 4rem)'
          }}
          modal
        >
          <TaskEmployeeDistributionCard
            visible={employeeDistributionDialogVisible}
            selectedDepartmentsFilter={selectedDepartmentsFilter as TreeNode[]}
            treeDropDownValue={treeDropDownValue}
            isDepartmentsLoaded={isDepartmentsLoaded}
            toastRef={toast}
            defaultDateFilter={selectedDateFilter}
            onClose={() => setEmployeeDistributionDialogVisible(false)}
          />
        </Dialog>
      )}

      <Dialog
        header="Карта"
        visible={mapDialogVisible}
        style={{
          minWidth: '72vw',
          width: '72vh',
          height: '85vh',
          minHeight: '85vh'
        }}
        contentStyle={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
        footer={mapDialogFooter}
        onHide={MapDialogCloseHandle}
        maximizable
      >
        <MapCard
          openTime={mapOpenTime}
          relevantCoordinates={relevantCoordinates}
          expiredCoordinates={expiredCoordinates}
          validTasks={validTasks}
          invalidTasks={invalidTasks}
          loading={loading}
          mapMessage={mapMessage}
          isMobile={isMobile}
        />
      </Dialog>

      <DeleteConfirmationDialog
        key="наряд"
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={confirmDeleteTemplate}
        entityType="наряд"
        entityName={name}
      />

      {isMobile ? (
        <>
          {header && (
            <div className="mb-3" style={{ flexShrink: 0 }}>
              {header}
            </div>
          )}
          <TaskMobileView
            tasks={tasks as any[]}
            loading={loading}
            onTaskClick={(task) => {
              setSelectedTask(task as any);
            }}
            selectedTaskId={selectedTask?.id}
            first={first}
            rows={pageSize}
            totalRecords={totalCount}
            onPage={onPageChanged}
            paginatorLeft={isMobile ? undefined : ' '}
            paginatorRight={
              isMobile
                ? undefined
                : `Всего: ${tasks.length > 0 ? totalCount : 0}`
            }
          />
        </>
      ) : (
        <DataTable
          filters={filters}
          paginator
          first={first}
          rows={pageSize}
          onPage={onPageChanged}
          totalRecords={totalCount}
          rowsPerPageOptions={[10, 25, 50, 100]}
          paginatorTemplate={
            'RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput'
          }
          currentPageReportTemplate="{currentPage} из {totalPages}"
          paginatorLeft={' '}
          paginatorRight={`Всего: ${tasks.length > 0 ? totalCount : 0}`}
          lazy
          multiSortMeta={multiSortMeta}
          onSort={setMultiSortMetaCallback}
          selectionMode="single"
          selection={selectedTask!}
          onSelectionChange={onSelectionChangeCallback}
          onRowDoubleClick={onRowDoubleClickCallback}
          metaKeySelection={false}
          scrollable
          sortMode="multiple"
          removableSort
          scrollHeight={scrollHeight}
          size="small"
          resizableColumns
          reorderableColumns
          value={tasks}
          dataKey="id"
          loading={loading}
          header={header}
          emptyMessage="Наряды не найдены."
          onRowReorder={onRowReorderCallback}
          onColumnResizeEnd={handleColumnResizeEnd}
          tableStyle={{ minWidth: '50rem' }}
          onFilter={onFiltersChange}
        >
          {columns}
        </DataTable>
      )}
    </div>
  );
}
